<template>
  <div class="checkbox-list">
    <div
      v-show="legend !== ''"
      class="legend v-centerer legend--bottom-margin"
    >
      <span>{{ legend }}</span>
    </div>
    <div class="checkbox-list__items">
      <Checkbox
        v-if="displayAllToggle"
        v-model="allToggle"
        class="checkbox-list__items__checkbox block"
        :title="$t('message.ALL')"
        :disabled="disabled"
      />
      <Checkbox
        v-for="item in itemList"
        :key="item.id"
        class="checkbox-list__items__checkbox block"
        :title="item.label"
        :value="item.value"
        :disabled="disabled"
        @input="onValueInput(item, $event)"
      />
    </div>
  </div>
</template>

<script>
/**
 * @typedef {Object} CheckboxItems
 * @property {number} id - Unique ID corresponding to the item
 * @property {string} label - Name that will be display next to the checkbox
 */
/**
 * Checkbox list - A list of checkbox with an 'All' checkbox toggle
 *
 * @vue-prop {Array} value - Checkbox that are checked will have its corresponding ID in this list
 * @vue-prop {[CheckboxItems]} items - List of CheckboxItems
 * @vue-prop {Boolean} [disabled] - Disable checkboxes
 * @vue-event {Array} input - Emit new 'value' prop, triggered when a checkbox is toggled
 */

// Import components
import Checkbox from '@/components/checkbox.vue';

export default {
  name: 'CheckboxList',

  components: {
    Checkbox,
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    labelKey: {
      type: String,
      default: 'label',
    },
    disabled: Boolean,
    displayAllToggle: {
      type: Boolean,
      default: true,
    },
    legend: {
      type: String,
      default: '',
    },
  },

  computed: {
    itemList() {
      return this.items.map(item => ({
        id: item.id,
        label: item[this.labelKey],
        value: this.value.includes(item.id),
      }));
    },
    allToggle: {
      get() {
        return this.itemList.every(item => item.value);
      },
      set(toggle) {
        this.$emit('input', toggle
          ? this.itemList.map(item => item.id)
          : []);
      },
    },
    selectedValues() {
      let selectedItems = '';
      this.items.forEach((item, index) => {
        if (this.value.includes(item.id)) {
          if (index !== this.items.length - 1) {
            selectedItems += `${item.label || item.name}, `;
          } else {
            selectedItems += item.label || item.name;
          }
        }
      });
      return selectedItems;
    },
  },

  methods: {
    /**
     * Event handler - Triggered when a checkbox is toggled
     *
     * @param {CheckboxItem} item - The corresponding CheckboxItem toggled
     * @param {boolean} toggle - New state of the checkbox
     */
    onValueInput(item, toggle) {
      const idx = this.value.indexOf(item.id);
      if (toggle && idx === -1) {
        this.$emit('input', this.value.concat(item.id));
      } else if (!toggle && idx !== -1) {
        this.$emit('input', this.value.filter(v => v !== item.id));
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.checkbox-list {

  &__items {
    width: 100%;

    & &__checkbox {
      position: relative;
    }
  }
}

</style>
